import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { omit, omitBy } from 'lodash';
import useLocalStorage from 'javascripts/hooks/useLocalStorage';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { parseISO } from 'date-fns';
import { formatDate } from 'helpers/intl-helper';
import Schema from 'javascripts/graphql/Schema';
import CCEvents, {
  EventNames,
  EventPropertyKeys,
  EventPropertyValues,
} from '@companycam/companycam-events';
import { Button } from '@companycam/slab-web';
import { translate } from 'translations/i18n';

import { useCurrentUser, useCurrentCompany } from 'javascripts/hooks';
import { assetsByDate } from 'helpers/asset-utils';
import { isNullOrEmpty } from 'helpers/utils';
import { graphQLIdFromProjectDBID } from 'helpers/graphql-utils';
import EmptyFeed, { Loading, Error, NoContent } from './EmptyFeed';
import AssetToolbar from 'components/shared/Layout/AssetToolbar';
import SelectCircle from 'components/shared/DesignSystem/SelectCircle';
import Grid from 'components/shared/DesignSystem/Grid';
import GalleryContainer from 'components/assets/GalleryContainer';
import Spinner from 'components/shared/LoadingSpinner';
import { DESC } from 'constants/sortDirections';
import PhotoUploadModal from '../projects/show/PhotoUploadModal';
import FilterRow from './FilterRow';
import { useMultiselect } from 'components/shared/forms/MultiselectPicker';
import SelectedAssetBar from './selectedAssetBar/SelectedAssetBar';
import useAssetFeed from './useAssetFeed';
import AssetFeedItem from './AssetFeedItem';
import UploadStatusCard from './UploadStatusCard';
import ViewMenu from './ViewMenu/ViewMenu';
import TagRow from './FilterRow/TagRow';
import { OR } from './constants';
import { useModal } from 'components/shared/hooks';
import CompanyEmptyAssetFeed from './CompanyEmptyAssetFeed';
import ProjectEmptyAssetFeed from './ProjectEmptyAssetFeed';

// #region styled-components

const Section = styled.div``;
const ScrollSection = styled.div`
  margin-bottom: 80px;
`;
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  ${({ enableSticky }) => (enableSticky ? 'position: sticky;' : '')}
  top: 0px;
  background: white;
  z-index: 1;
`;
const SectionTitle = styled.p`
  margin: 0 0 0 8px;
  font-weight: 600;
  font-size: 18px;
  font-family: 'Averta', system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;
const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 16px 0;
`;
const GridItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  margin: 8px 0 0 0;
`;
const Creator = styled.a`
  margin: 0;
  font-size: var(--cc_size_text_xs);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--cc_color_text_nonessential);

  &:hover {
    text-decoration: underline;
  }
`;
const CreatorNoLink = styled.span`
  margin: 0;
  color: var(--cc_color_text_nonessential);
  font-size: var(--cc_size_text_xs);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ProjectName = styled.a`
  margin: 0 0 var(--cc_size_spacing_xxs);
  font-weight: 500;
  color: var(--cc_color_text_default);
  font-size: calc(var(--cc_size_text_xs) + 1px);
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    text-decoration: underline;
  }
`;

const AssetToolbarLayout = styled.div`
  margin-bottom: var(--cc_size_spacing_m);
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);

  @media screen and (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const FilterControlsLayout = styled.div``;

const ViewAddButtonGroup = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: var(--cc_size_spacing_s);
  height: 100%;
  order: -1;
  align-self: start;

  @media screen and (min-width: 768px) {
    order: 2;
  }
`;

const AddPhotosButtonLayout = styled.div`
  align-self: start;
`;

// #endregion

const SECTION_DATE_FORMAT = 'PPPP';

const assetDateFormat = (asset) => formatDate(parseISO(asset.capturedAt), 'p');

const BLANK_FILTERS = {
  assetType: '',
  capturedAfter: null,
  capturedBefore: null,
  projects: [],
  users: [],
  groups: [],
  tags: [],
};

const AssetFeed = ({
  entityId,
  isPresentedInModal,
  multiSelectHandlerComponent,
  projectDatabaseId,
  enableTopControls,
  enableMultiselect,
  enableStickySectionHeaders,
  enableAssetMenu,
  initialFilters,
  maxSelectionCount,
}) => {
  const currentUser = useCurrentUser();
  const { currentCompany } = useCurrentCompany();
  const { showModal, Modal } = useModal();
  const [sortOrder, setSortOrder] = useLocalStorage('assetFeedSortDir', DESC);
  const [filters, setFilters] = useState(initialFilters);
  const [paginationLocked, setPaginationLocked] = useState(false);
  const [assetSize, setAssetSize] = useLocalStorage(
    'assetFeedGridItemSize',
    'small',
  );
  const [showTags, setShowTags] = useLocalStorage('assetFeedShowTags', true);

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [pendingUploadCount, setPendingUploadCount] = useState(0);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [initialGalleryAsset, setInitialGalleryAsset] = useState(null);
  const projectNodeId = graphQLIdFromProjectDBID(projectDatabaseId);

  /**
   * As BLANK_FILTERS and initialFilters are used to determine the GQL query, we need this separate "intermediate" filter state
   * to apply when the user clicks Clear All - without affecting the initial data load.
   *
   * Clearing filters should respect whatever the initial assetType is (in initialFilters) but should reset all other
   * filters to the BLANK_FILTERS state.
   */
  const clearedFilters = {
    ...BLANK_FILTERS,
    assetType: initialFilters.assetType || null,
  };

  const [tagsOperator, setTagsOperator] = useState(OR);

  const {
    loading: isLoadingAssets,
    error,
    assets,
    waitingAssetCount,
    parent,
    hasNextPage,
    fetchNextPage,
  } = useAssetFeed({
    entityId,
    sortOrder,
    filters,
    tagsOperator,
  });

  const { __typename: parentType } = parent;
  const currentlyOnProject = parentType === Schema.Types.Project;

  const [ref, bottomOfFeedInView] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 300px 0px',
  });

  const assetSections = useMemo(
    () => assetsByDate({ assets, sectionDateFormat: SECTION_DATE_FORMAT }),
    [assets],
  );

  const hasFilters = useMemo(() => {
    return Object.keys(omitBy(filters, isNullOrEmpty)).length > 0;
  }, [filters]);

  /**
   * We only want to use this filter detection logic inside the FilterRow, otherwise the FilterRow can be hidden
   * and cause UI and UX issues for the end user. It's defined here for ease of organization
   */
  const hasNonAssetTypeFilters = useMemo(() => {
    const filtersWithoutAssetType = omit(filters, 'assetType');
    return (
      Object.keys(omitBy(filtersWithoutAssetType, isNullOrEmpty)).length > 0
    );
  }, [filters]);

  const handleFetchNextPageOnScroll = useCallback(async () => {
    if (
      hasNextPage &&
      !isLoadingAssets &&
      !paginationLocked &&
      bottomOfFeedInView
    ) {
      setPaginationLocked(true);
      await fetchNextPage();

      // This kinda sucks but it works around the
      // IntersectionObserver attempting to immediately
      // trigger another intersection event.
      setTimeout(() => {
        setPaginationLocked(false);
      }, 3500);
    }
  }, [
    hasNextPage,
    isLoadingAssets,
    paginationLocked,
    bottomOfFeedInView,
    fetchNextPage,
  ]);

  // eslint-disable-next-line local-rules/no-unnecessary-functions
  useEffect(() => {
    handleFetchNextPageOnScroll();
  }, [
    isLoadingAssets,
    paginationLocked,
    bottomOfFeedInView,
    hasNextPage,
    fetchNextPage,
    handleFetchNextPageOnScroll,
  ]);

  const handleGalleryFetchNextPage = useCallback(async () => {
    if (hasNextPage && !isLoadingAssets) {
      await fetchNextPage();
    }
  }, [hasNextPage, isLoadingAssets, fetchNextPage]);

  const handleCloseGallery = () => {
    setIsGalleryOpen(false);
    setInitialGalleryAsset(null);
  };

  const openAssignUsersModal = () => {
    showModal(Modal.PROJECT_USERS, {
      projectId: projectDatabaseId,
      openToAssignUsers: true,
    });
  };

  const multiSelectProps = useMultiselect({
    selectableItems: assets,
    maxSelectionCount,
  });

  const {
    checkSelected,
    selectionCount,
    getAllSelected,
    clearSelection,
    toggleSelected,
    checkGroupSelected,
    toggleGroupSelected,
  } = multiSelectProps;

  const applyFilter = (incomingFilter = null) => {
    CCEvents.logEvent(EventNames.FILTER, {
      [EventPropertyKeys.MEDIA]: EventPropertyValues.PHOTO,
    });
    setFilters({ ...filters, ...incomingFilter });
  };

  const renderAsset = (asset) => {
    const assetCompanyId = asset.contributor.companyId || null;
    const assetFromDifferentCompany = assetCompanyId
      ? currentCompany.node_id !== assetCompanyId
      : false;
    const baseCreatorMetadata = `${assetDateFormat(asset)} • ${
      asset.contributor.name
    }`;
    let creatorMetadata;
    if (assetFromDifferentCompany) {
      creatorMetadata = `${baseCreatorMetadata} (${asset.contributor.companyName})`;
    } else {
      creatorMetadata = baseCreatorMetadata;
    }
    return (
      <GridItem key={asset.id}>
        <AssetFeedItem
          asset={asset}
          selected={checkSelected(asset)}
          onClick={() => {
            setInitialGalleryAsset(asset);
            setIsGalleryOpen(true);
          }}
          toggleSelected={toggleSelected}
          enableSelection={enableMultiselect}
          enableMenu={enableAssetMenu}
          assetSize={assetSize}
        />
        <GridItemMeta
          className="hint--top no-transition"
          aria-label={asset.projectInfo.formattedLine2}
        >
          {parentType !== 'Project' && (
            <ProjectName href={`/projects/${asset.projectInfo.databaseId}`}>
              {asset.projectInfo.formattedLine1}
            </ProjectName>
          )}
          {asset.contributor.webURL ? (
            <Creator href={asset.contributor.webURL}>{creatorMetadata}</Creator>
          ) : (
            <CreatorNoLink>{creatorMetadata}</CreatorNoLink>
          )}
        </GridItemMeta>
      </GridItem>
    );
  };

  const renderAssetSection = (section, index) => (
    <Section key={section.key}>
      <SectionHeader enableSticky={enableStickySectionHeaders}>
        <SelectCircle
          selected={checkGroupSelected(section.assets)}
          onClick={() => {
            toggleGroupSelected(section.assets);
          }}
          visible={enableMultiselect}
          isDatePicker
          data-intercom-target={`assetfeed__select-photo-group-${index}`}
          data-testid={`assetfeed__select-photo-group-${index}`}
        />
        <SectionTitle>{section.title}</SectionTitle>
      </SectionHeader>

      <Grid size={assetSize}>{section.assets.map(renderAsset)}</Grid>
    </Section>
  );

  const renderAddPhotosButton = (opts = {}) => {
    const toolbarButton = !!opts.toolbarButton;

    return (
      <Button
        color="primary"
        icon={{
          name: 'plus-circle-outline',
          position: 'before',
        }}
        onClick={() => setUploadModalVisible(true)}
        size={toolbarButton ? 'medium' : 'large'}
        data-testid="photos__add-new-photo"
      >
        <span>{translate('assetFeed.filterRow.uploadPhotos')}</span>
      </Button>
    );
  };

  const renderEmptyState = () => {
    if (hasFilters) {
      return (
        <EmptyFeed
          message={translate('assetFeed.emptyState.emptyFilterList')}
        />
      );
    }

    if (parentType.toLowerCase() === 'company') {
      return <CompanyEmptyAssetFeed />;
    }

    if (parentType.toLowerCase() === 'project') {
      return (
        <ProjectEmptyAssetFeed
          openAssignUsersModal={openAssignUsersModal}
          setUploadModalVisible={setUploadModalVisible}
        />
      );
    }

    return <NoContent type={parentType} />;
  };

  const renderNoMorePages = () => {
    return (
      <div className="photo-feed-end">
        <p>{translate('assetFeed.noMorePhotos')}</p>
      </div>
    );
  };

  const renderUploadModal = () => (
    <PhotoUploadModal
      isOpen={uploadModalVisible}
      close={() => setUploadModalVisible(false)}
      user={currentUser}
      projectId={projectDatabaseId}
      onPendingUploadCountChange={setPendingUploadCount}
    />
  );

  const renderUploadStatus = () => (
    <UploadStatusCard
      waitingProcessingCount={waitingAssetCount}
      pendingUploadCount={Math.max(pendingUploadCount, 0)}
    />
  );

  const isLoading = isLoadingAssets && !assets.length;
  const isLoadingPagination =
    (isLoadingAssets || paginationLocked) && assets.length;
  const hasNoMorePages = !isLoadingAssets && assets.length > 0 && !hasNextPage;

  const renderContent = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (error) {
      const { graphQLErrors } = error;
      const { message } = graphQLErrors[0];
      return <Error message={message} />;
    }

    if (!isLoadingAssets && assets.length === 0) {
      return renderEmptyState();
    }

    const contentBody = (
      <Fragment>
        {assetSections.map(renderAssetSection)}
        <div ref={ref} style={{ height: '1px', opacity: 0 }} />
        {isLoadingPagination ? <Spinner /> : null}
        {hasNoMorePages ? renderNoMorePages() : null}
      </Fragment>
    );

    const content = isPresentedInModal ? (
      <ScrollSection>{contentBody}</ScrollSection>
    ) : (
      contentBody
    );

    return content;
  };

  return (
    <React.Fragment>
      {isPresentedInModal &&
      multiSelectHandlerComponent &&
      React.isValidElement(multiSelectHandlerComponent)
        ? React.cloneElement(multiSelectHandlerComponent, multiSelectProps)
        : null}
      <AssetToolbar
        hidden={
          !enableTopControls ||
          (!isPresentedInModal && !hasFilters && assets.length === 0)
        }
      >
        <AssetToolbarLayout>
          <ViewAddButtonGroup data-testid="project__asset-toolbar__view-and-add-photos">
            <ViewMenu
              setAssetSize={setAssetSize}
              setSortOrder={setSortOrder}
              setTagsOperator={setTagsOperator}
              tagsOperator={tagsOperator}
              assetSize={assetSize}
              sortOrder={sortOrder}
              showTags={showTags}
              setShowTags={setShowTags}
              setAssetType={applyFilter}
              selectedAssetType={filters.assetType}
              hideProjects={currentlyOnProject}
            />

            {currentlyOnProject ? (
              <AddPhotosButtonLayout>
                {renderAddPhotosButton({ toolbarButton: true })}
              </AddPhotosButtonLayout>
            ) : null}
          </ViewAddButtonGroup>

          <FilterControlsLayout>
            <FilterRow
              projectId={projectDatabaseId}
              currentFilters={filters}
              onChange={setFilters}
              hideProjects={currentlyOnProject}
              hideUsers={parentType === Schema.Types.User}
              hideGroups={parentType === Schema.Types.Group}
              clearFilters={() => {
                setFilters(clearedFilters);
              }}
              hasFilters={hasNonAssetTypeFilters}
              applyFilter={applyFilter}
              showTags={showTags}
            />
          </FilterControlsLayout>
        </AssetToolbarLayout>

        {currentlyOnProject && showTags ? (
          <TagRow
            projectId={projectDatabaseId}
            activeFilterTags={filters.tags}
            onChange={applyFilter}
          />
        ) : null}
      </AssetToolbar>

      {parentType === 'Project' && renderUploadStatus()}
      {renderContent()}
      {renderUploadModal()}

      <GalleryContainer
        initialAsset={initialGalleryAsset}
        assets={assets}
        onRequestClose={handleCloseGallery}
        isOpen={isGalleryOpen}
        onEndReached={handleGalleryFetchNextPage}
        onEndReachedThreshold={10}
      />

      {!multiSelectHandlerComponent && (
        <SelectedAssetBar
          getAllSelected={getAllSelected}
          selectionCount={selectionCount}
          onRequestClose={clearSelection}
          projectNodeId={currentlyOnProject ? projectNodeId : null}
          projectDatabaseId={projectDatabaseId}
        />
      )}
    </React.Fragment>
  );
};

AssetFeed.propTypes = {
  entityId: PropTypes.string.isRequired,
  isPresentedInModal: PropTypes.bool,
  multiSelectHandlerComponent: PropTypes.node,
  projectDatabaseId: PropTypes.number,
  enableTopControls: PropTypes.bool,
  enableMultiselect: PropTypes.bool,
  enableStickySectionHeaders: PropTypes.bool,
  enableAssetMenu: PropTypes.bool,
  initialFilters: PropTypes.object,
  maxSelectionCount: PropTypes.number,
};

AssetFeed.defaultProps = {
  isPresentedInModal: false,
  multiSelectHandlerComponent: null,
  projectDatabaseId: null,
  enableTopControls: true,
  enableMultiselect: true,
  enableStickySectionHeaders: true,
  enableAssetMenu: true,
  initialFilters: BLANK_FILTERS,
};

export default AssetFeed;
